import { Box, Typography, Avatar, Menu, MenuItem } from "@mui/material";
import { KeyboardArrowDown, Logout } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";

import muzukuru2 from "../../../assets/muzukuru2.png";
import { RootState } from "../../../store/Index";
import {
  ReferralUser,
  clearReferralAuth,
} from "../../../store/slice/ReferralAuthSlice";
import ReferralNotificationBell from "../referrals-dashboard/components/ReferralNotificationBell";

export default function ReferralNavbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(
    (state: RootState) => state.referralAuth.user,
  ) as ReferralUser | null;

  const fullName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.username ||
    "—";
  const email = user?.email || user?.username || "";
  const avatarInitials =
    [user?.first_name?.[0], user?.last_name?.[0]]
      .filter(Boolean)
      .join("")
      .toUpperCase() || "?";

  // ── Live clock ────────────────────────────────────────────────────────────
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const dateString = now.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
  const timeString = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });

  // ── Scroll-aware background ───────────────────────────────────────────────
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Dropdown Menu State ───────────────────────────────────────────────────
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    dispatch(clearReferralAuth());
    navigate("/referral-program");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, md: 4 },
        pt: 4,
        pb: 2,
        position: "sticky",
        top: 0,
        zIndex: 100,
        width: "100%",
        boxSizing: "border-box",
        backgroundColor: scrolled ? "rgba(255, 255, 255, 0.8)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.05)" : "none",
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Logo */}
      <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        <img
          src={muzukuru2}
          alt="Muzukuru"
          style={{ height: 66, width: "auto", cursor: "pointer" }}
          onClick={() => navigate("/referral-program")}
        />
      </Box>

      {/* Right side controls */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}
      >
        {/* Notification Bell */}
        <ReferralNotificationBell />

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            whiteSpace: "nowrap",
            fontSize: "0.9rem",
            display: { xs: "none", md: "block" },
          }}
        >
          {dateString}, {timeString}
        </Typography>

        {/* User Profile Trigger Box */}
        <Box
          onClick={handleMenuOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            p: 0.5,
            borderRadius: 2,
            transition: "background-color 0.2s",
            "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
          }}
        >
          <Avatar
            src={user?.profile_picture_url || undefined}
            sx={{
              width: 42,
              height: 42,
              bgcolor: "#E8D5C4",
              color: "#7B5648",
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            {!user?.profile_picture_url && avatarInitials}
          </Avatar>

          <Box
            sx={{
              display: "none",
              flexDirection: "column",
              "@media (min-width:700px)": { display: "flex" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body1" fontWeight={600} lineHeight={1.3}>
                {fullName}
              </Typography>
              <KeyboardArrowDown
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" lineHeight={1.3}>
              {email}
            </Typography>
          </Box>
        </Box>

        {/* Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          disableAutoFocusItem={true}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 160,
              bgcolor: "background.paper",
              borderRadius: 2,
              overflow: "visible",
              "&::before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
        >
          <MenuItem
            onClick={handleLogout}
            sx={{ gap: 1.5, color: "error.main" }}
          >
            <Logout fontSize="small" />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
